import { DateTime, Info } from "luxon";
import { ReactElement } from "react";
import "./index.scss";

const longDaysOfWeek = Info.weekdays("long");
const sortedLongDaysOfWeek = [longDaysOfWeek[6], ...longDaysOfWeek.slice(0, 6)];

const shortDaysOfWeek = Info.weekdays("short");
const sortedShortDaysOfWeek = [
  shortDaysOfWeek[6],
  ...shortDaysOfWeek.slice(0, 6),
];

const Calendar = ({
  month,
  year,
  today,
  startDate,
  endDate,
  onClick,
  unavailableDates = [],
}: {
  month: number;
  year: number;
  today: string;
  startDate: string;
  endDate: string;
  onClick: (date: string) => void;
  unavailableDates: string[];
}) => {
  const todayLuxon = DateTime.fromISO(today);
  const monthStartLuxon = DateTime.fromObject({ month, year, day: 1 });
  const startDateLuxon = DateTime.fromISO(startDate);
  const endDateLuxon = DateTime.fromISO(endDate);

  const startOfMonth = DateTime.fromObject({ month, year, day: 1 });

  const numberOfDays = startOfMonth.endOf("month").day;
  const startOfMonthOffset = startOfMonth.weekday % 7;
  const monthName = monthStartLuxon.toLocaleString({
    month: "long",
  });

  const tableHeaderNodes = sortedShortDaysOfWeek.map(
    (shortDayOfWeek, shortDayOfWeekIndex) => {
      return (
        <th key={shortDayOfWeek}>
          <span className="visually-hidden">
            {sortedLongDaysOfWeek[shortDayOfWeekIndex]}
          </span>
          <span aria-hidden={true}>{shortDayOfWeek}</span>
        </th>
      );
    }
  );

  const tableBodyNodes: ReactElement<
    React.HTMLAttributes<HTMLTableSectionElement>
  >[] = [];
  let currentTableRowChildren: ReactElement<
    React.HTMLAttributes<HTMLTableCellElement>
  >[] = [];

  for (
    let currentOffsetDay = 1;
    currentOffsetDay <= startOfMonthOffset;
    currentOffsetDay++
  ) {
    currentTableRowChildren.push(
      <td key={`offset-start-${currentOffsetDay}`}></td>
    );
  }

  for (let currentDay = 1; currentDay <= numberOfDays; currentDay++) {
    const dateLuxon = DateTime.fromObject({ month, day: currentDay, year });

    const isBeforeToday = dateLuxon < todayLuxon;
    const isToday = dateLuxon.equals(todayLuxon);
    const isStartDate = dateLuxon.equals(startDateLuxon);
    const isEndDate = dateLuxon.equals(endDateLuxon);
    const isBetweenStartDateAndEndDate =
      dateLuxon > startDateLuxon && dateLuxon < endDateLuxon;
    const isUnavailable = unavailableDates.includes(dateLuxon.toISODate()!);
    const availabilityStatus =
      isBeforeToday || isUnavailable ? "is-not-available" : "available";

    currentTableRowChildren.push(
      <td
        aria-label={`${dateLuxon.toLocaleString({
          weekday: "long",
          month: "long",
          day: "numeric",
        })}, ${availabilityStatus}`}
        className={`${availabilityStatus} ${
          isBeforeToday ? "before-today" : ""
        } ${isToday ? "today" : ""} ${isStartDate ? "start-date" : ""} ${
          isEndDate ? "end-date" : ""
        } ${
          isBetweenStartDateAndEndDate ? "between-start-date-and-end-date" : ""
        }`}
        onClick={() => {
          if (isBeforeToday === false) {
            onClick(dateLuxon.toISODate()!);
          }
        }}
        key={currentDay}
      >
        <div className="day-wrapper">{currentDay}</div>
      </td>
    );

    if (currentDay === numberOfDays) {
      const endOfMonthOffset = 7 - currentTableRowChildren.length;
      for (
        let currentOffsetDay = 1;
        currentOffsetDay <= endOfMonthOffset;
        currentOffsetDay++
      ) {
        currentTableRowChildren.push(
          <td key={`offset-end-${currentOffsetDay}`}></td>
        );
      }
    }
    if (currentTableRowChildren.length === 7) {
      tableBodyNodes.push(<tr key={currentDay}>{currentTableRowChildren}</tr>);
      currentTableRowChildren = [];
    }
  }

  return (
    <div className="component calendar">
      <div className="calendar-header">
        <label>
          <span className="month">{monthName}</span>{" "}
          <span className="year">{startOfMonth.toFormat("yyyy")}</span>
        </label>
      </div>
      <table>
        <thead>
          <tr>{tableHeaderNodes}</tr>
        </thead>
        <tbody>{tableBodyNodes}</tbody>
      </table>
    </div>
  );
};

export default Calendar;
